import { DateField, TimeField } from '@attendance/ui';
import { useDateLabels } from '../../components/common/dateFieldLabels.js';
import { useCompanyToday } from '../../hooks/useFormatDate.js';
import type { ControlProps } from './SettingsControls.js';

/* ===========================================================================
 * THE TWO CLOCK-AND-CALENDAR CONTROLS, and why neither is a native input.
 *
 * `<input type="date">` takes its DISPLAY ORDER and its typeface from the
 * reader's OPERATING SYSTEM, so a settings row could write `09 / 01 / 2026`
 * on a machine where every table beside it writes `01-09-2026` — one fact, two
 * orders, and no test in the app could see it because the order was never in
 * the app. `DateField` and `TimeField` draw their own text: day-first dates,
 * 24-hour times with no meridiem toggle.
 *
 * WHAT DID NOT CHANGE, and must not: the VALUE is still a plain `YYYY-MM-DD`
 * day key / `HH:MM` wall-clock reading, never an instant, so the browser's zone
 * cannot shift which day or which minute the reader picked
 * (docs/time-and-dates.md).
 *
 * THE ACCESSIBLE NAME is the field's label, passed explicitly. The trigger is a
 * button, and `DateTrigger` falls back to a generic "Choose a date" when it is
 * given no name of its own — which would replace "Ramadan starts" with a
 * sentence that names no setting. `FieldRow`'s `<Label htmlFor>` still points
 * at it for the pointer; this is what a screen reader announces.
 * ======================================================================== */
export function DateControl({ inputId, value, disabled, onChange, label }: ControlProps): JSX.Element {
  /* `today` is a PROP with no default: the picker has no clock of its own and
     must not grow one. This is the company's day (design-rules §3.1), not the
     device's. */
  const today = useCompanyToday();
  return (
    <DateField
      id={inputId}
      value={typeof value === 'string' ? value : ''}
      today={today}
      disabled={disabled}
      aria-label={label}
      labels={useDateLabels()}
      onChange={(next) => onChange(next)}
    />
  );
}

export function TimeControl({ inputId, value, disabled, onChange, label }: ControlProps): JSX.Element {
  return (
    <TimeField
      id={inputId}
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      aria-label={label}
      labels={useDateLabels()}
      onChange={(next) => onChange(next)}
    />
  );
}
